'use client'

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Toggle } from '@/components/ui/toggle'
import { useUserSettings } from '@/components/user-settings-context'
import { trpc } from '@/lib/trpc'

interface QuestionSetting {
  id: string
  questionText: string
  isEnabled: boolean
  sortOrder: number
}

function SortableQuestionRow({
  question,
  onToggle,
}: {
  question: QuestionSetting
  onToggle: (id: string, enabled: boolean) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between gap-2 py-2"
    >
      <span className="flex-1 text-sm text-foreground">
        {question.questionText}
      </span>
      <div className="flex items-center gap-1">
        <Toggle
          checked={question.isEnabled}
          onChange={(v) => onToggle(question.id, v)}
          aria-label={`Toggle "${question.questionText}"`}
        />
        <button
          type="button"
          className="cursor-grab touch-none active:cursor-grabbing"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <GripVertical className="text-muted-foreground h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export function PostmortemSettings() {
  const { settings, updateSettings } = useUserSettings()
  const utils = trpc.useUtils()
  const { data } = trpc.postmortem.getQuestions.useQuery()

  const [questions, setQuestions] = useState<QuestionSetting[]>([])

  const saveMutation = trpc.postmortem.saveQuestionSettings.useMutation({
    onSuccess: () => {
      utils.postmortem.getQuestions.invalidate()
    },
    onError: () => {
      // toggleQuestion/handleDragEnd apply the change to local `questions`
      // optimistically, so a silent failure would leave the UI showing an
      // unsaved edit that reverts on the next load with no explanation. Undo it
      // and surface the error. Invalidate alone can't revert it: the optimistic
      // change lives only in local state (the query cache still holds the
      // server payload), and React Query's structural sharing keeps `data`'s
      // reference stable across the refetch, so the [data] effect never
      // re-runs. Reset from the cached server snapshot explicitly, then
      // invalidate to pull any genuine server change. [swallowed-exception]
      const server = utils.postmortem.getQuestions.getData()
      if (server?.questions) setQuestions(server.questions)
      utils.postmortem.getQuestions.invalidate()
      toast.error('Could not save your postmortem settings. Please try again.')
    },
  })

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  useEffect(() => {
    if (data?.questions) {
      setQuestions(data.questions)
    }
  }, [data])

  const persistSettings = useCallback(
    (updated: QuestionSetting[]) => {
      saveMutation.mutate({
        settings: updated.map((q) => ({
          questionId: q.id,
          isEnabled: q.isEnabled,
          sortOrder: q.sortOrder,
        })),
      })
    },
    [saveMutation],
  )

  const toggleQuestion = (id: string, enabled: boolean) => {
    const updated = questions.map((q) =>
      q.id === id ? { ...q, isEnabled: enabled } : q,
    )
    setQuestions(updated)
    persistSettings(updated)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setQuestions((prev) => {
      const oldIndex = prev.findIndex((q) => q.id === active.id)
      const newIndex = prev.findIndex((q) => q.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return prev

      const reordered = arrayMove(prev, oldIndex, newIndex).map((q, i) => ({
        ...q,
        sortOrder: i,
      }))
      persistSettings(reordered)
      return reordered
    })
  }

  return (
    <div className="space-y-1">
      {/* Difficulty rating toggle */}
      <div className="flex items-center justify-between py-3">
        <span className="text-sm text-foreground">Show difficulty rating</span>
        <Toggle
          checked={settings.postmortem?.showDifficultyRating ?? true}
          onChange={(v) =>
            updateSettings({ postmortem: { showDifficultyRating: v } })
          }
          aria-label="Toggle difficulty rating"
        />
      </div>

      <div className="border-border border-t pt-3">
        <h3 className="text-muted-foreground mb-2 text-xs font-medium">
          Reflection Questions
        </h3>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={questions.map((q) => q.id)}
            strategy={verticalListSortingStrategy}
          >
            {questions.map((q) => (
              <SortableQuestionRow
                key={q.id}
                question={q}
                onToggle={toggleQuestion}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  )
}
